import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
/**
 * OAuth device-flow approval page. Lives at `/device` and handles the
 * human half of the daemon's device-registration handshake — used by
 * both kritaka-pty-daemon (legacy Claude Code path) and the new
 * kritaka-agent-runtime macOS app shell (KTK-250):
 *
 *   daemon → POST /api/device-registration/start → {user_code, verification_uri}
 *   daemon → opens browser to `verification_uri?code=<user_code>`  ← this page
 *   user   → signs in (outer Clerk gate) → clicks Approve here
 *   daemon → POST /api/device-registration/poll → {status:'approved', device_token, ...}
 *
 * The web never sees the device token; the daemon picks it up via /poll.
 * See rocky's spec in #client-dev (2026-04-22) for the endpoint contract.
 */
export function DevicePage({ hub }) {
    const initialCode = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("code") ?? "";
    }, []);
    if (!initialCode) {
        return _jsx(ManualCodeEntry, {});
    }
    return _jsx(ApprovalFlow, { hub: hub, userCode: initialCode });
}
function ManualCodeEntry() {
    const [value, setValue] = useState("");
    return (_jsxs(PageShell, { children: [_jsx("h2", { style: { margin: 0, fontSize: 18, color: "var(--text-primary)" }, children: "Pair a device" }), _jsx("p", { style: hintStyle, children: "Enter the code shown by the Kritaka Agent Runtime app when you sign in." }), _jsxs("form", { onSubmit: (e) => {
                    e.preventDefault();
                    const trimmed = value.trim();
                    if (!trimmed)
                        return;
                    window.location.href = `/device?code=${encodeURIComponent(trimmed)}`;
                }, style: { display: "flex", gap: 8 }, children: [_jsx("input", { type: "text", value: value, onChange: (e) => setValue(e.target.value.toUpperCase()), placeholder: "ABCD-1234", autoFocus: true, style: {
                            ...inputStyle,
                            flex: 1,
                            fontFamily: "monospace",
                            letterSpacing: 2,
                        } }), _jsx("button", { type: "submit", disabled: !value.trim(), style: {
                            ...primaryBtn,
                            background: value.trim() ? "var(--accent)" : "var(--bg-hover)",
                            cursor: value.trim() ? "pointer" : "not-allowed",
                        }, children: "Continue" })] })] }));
}
function ApprovalFlow({ hub, userCode }) {
    const [registration, setRegistration] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [submitting, setSubmitting] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [approved, setApproved] = useState(false);
    const [denied, setDenied] = useState(false);
    useEffect(() => {
        let cancelled = false;
        // KTK-253 — device registrations are now user-scoped, so we no
        // longer need to fetch workspaces or pick one to submit. The
        // single fetch is the registration itself.
        hub
            .getDeviceRegistrationByCode(userCode)
            .then((reg) => {
            if (cancelled)
                return;
            setRegistration(reg);
        })
            .catch((e) => {
            if (!cancelled)
                setLoadError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [hub, userCode]);
    if (loadError) {
        return (_jsx(PageShell, { children: _jsx(TerminalState, { title: "Couldn't look up device code", body: loadError, tone: "error" }) }));
    }
    if (!registration) {
        return (_jsx(PageShell, { children: _jsx("div", { style: { color: "var(--text-muted)", fontSize: 13 }, children: "Loading\u2026" }) }));
    }
    // Terminal states — nothing to approve or deny.
    if (approved || registration.status === "approved") {
        return (_jsx(PageShell, { children: _jsx(TerminalState, { title: "Device approved", body: "You can close this tab. The Kritaka Agent Runtime is finishing setup.", tone: "success" }) }));
    }
    if (denied || registration.status === "denied") {
        return (_jsx(PageShell, { children: _jsx(TerminalState, { title: "Device denied", body: "You rejected this registration request. The runtime will exit the registration flow.", tone: "muted" }) }));
    }
    if (registration.status === "consumed") {
        return (_jsx(PageShell, { children: _jsx(TerminalState, { title: "Already registered", body: "This code has already been redeemed by the runtime. Nothing more to do here.", tone: "muted" }) }));
    }
    if (registration.status === "expired") {
        return (_jsx(PageShell, { children: _jsx(TerminalState, { title: "Code expired", body: "This code timed out. Open the Kritaka Agent Runtime app and sign in again to get a new code.", tone: "error" }) }));
    }
    // Pending — render the approval card.
    const expiresAt = new Date(registration.expires_at);
    const expiresInMs = expiresAt.getTime() - Date.now();
    const expiresMinutes = Math.max(0, Math.round(expiresInMs / 60_000));
    return (_jsxs(PageShell, { children: [_jsxs("div", { children: [_jsxs("h2", { style: { margin: 0, fontSize: 18, color: "var(--text-primary)" }, children: ["Approve ", _jsx("strong", { children: registration.display_name }), "?"] }), _jsxs("div", { style: { marginTop: 6, fontSize: 12, color: "var(--text-muted)" }, children: [platformLabel(registration.platform), " \u00B7 ", registration.hostname] })] }), _jsxs("p", { style: hintStyle, children: ["The device above is asking to connect to Kritaka with ", _jsx("strong", { children: "your" }), " user credentials. Once approved, it can run agents under your account in any workspace you're a member of. Code expires in ~", expiresMinutes, "\u00A0min."] }), submitError && _jsx("div", { style: errorBox, children: submitError }), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8 }, children: [_jsx("button", { type: "button", onClick: async () => {
                            setSubmitting("deny");
                            setSubmitError(null);
                            try {
                                await hub.denyDeviceRegistration(userCode);
                                setDenied(true);
                            }
                            catch (e) {
                                setSubmitError(e instanceof Error ? e.message : String(e));
                            }
                            finally {
                                setSubmitting(null);
                            }
                        }, disabled: submitting !== null, style: {
                            ...secondaryBtn,
                            cursor: submitting ? "not-allowed" : "pointer",
                            opacity: submitting ? 0.6 : 1,
                        }, children: submitting === "deny" ? "Denying…" : "Deny" }), _jsx("button", { type: "button", disabled: submitting !== null, onClick: async () => {
                            setSubmitting("approve");
                            setSubmitError(null);
                            try {
                                await hub.approveDeviceRegistration(userCode);
                                setApproved(true);
                            }
                            catch (e) {
                                setSubmitError(e instanceof Error ? e.message : String(e));
                            }
                            finally {
                                setSubmitting(null);
                            }
                        }, style: {
                            ...primaryBtn,
                            background: submitting !== null ? "var(--bg-hover)" : "var(--accent)",
                            cursor: submitting !== null ? "not-allowed" : "pointer",
                        }, children: submitting === "approve" ? "Approving…" : "Approve" })] })] }));
}
function PageShell({ children }) {
    return (_jsx("div", { className: "app-pre-shell", style: {
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "var(--bg-primary)",
            padding: 24,
        }, children: _jsxs("div", { style: {
                width: "100%",
                maxWidth: 480,
                background: "var(--bg-secondary)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
            }, children: [_jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", letterSpacing: 1 }, children: "KRITAKA \u00B7 DEVICE PAIRING" }), children] }) }));
}
function TerminalState({ title, body, tone, }) {
    const color = tone === "success"
        ? "var(--success)"
        : tone === "error"
            ? "var(--error)"
            : "var(--text-muted)";
    return (_jsxs("div", { children: [_jsx("h2", { style: { margin: 0, fontSize: 18, color }, children: title }), _jsx("p", { style: { ...hintStyle, marginTop: 8 }, children: body })] }));
}
function platformLabel(p) {
    if (p === "darwin")
        return "macOS";
    if (p === "win32")
        return "Windows";
    return "Linux";
}
const hintStyle = {
    fontSize: 13,
    color: "var(--text-secondary)",
    margin: 0,
    lineHeight: 1.5,
};
const inputStyle = {
    padding: "8px 12px",
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
};
const primaryBtn = {
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    color: "white",
    fontFamily: "inherit",
};
const secondaryBtn = {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    border: "1px solid var(--border)",
    borderRadius: 6,
    background: "transparent",
    color: "var(--text-secondary)",
    fontFamily: "inherit",
};
const errorBox = {
    color: "var(--error)",
    fontSize: 12,
    padding: 8,
    border: "1px solid rgba(250,82,82,0.3)",
    borderRadius: 6,
};
//# sourceMappingURL=DevicePage.js.map